import React, { useMemo, useState } from "react";
import dayjs from "dayjs";

import { formatCurrency, formatShortMoney } from "./dashboardUtils";

export default function InventoryValueChart({
  detailData,
  isLoading,
  periodType,
}) {
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const fontStyle = { fontFamily: "'Inter', sans-serif" };

  const periods = useMemo(() => {
    return detailData?.periods || [];
  }, [detailData]);

  // Chuẩn hóa danh sách điểm trên biểu đồ
  const chartData = useMemo(() => {
    return periods.map((p) => {
      let label = p.period;
      if (periodType === "day") {
        label = dayjs(p.period).format("DD/MM");
      } else if (periodType === "month") {
        label = `T${dayjs(p.period).format("MM/YY")}`;
      } else if (periodType === "year") {
        label = p.period;
      }
      return {
        key: p.period,
        label,
        period: p.period,
        value_date: p.value_date || p.period,
        amount: Number(p.total_amount) || 0,
      };
    });
  }, [periods, periodType]);

  // Thống kê trục Y (3 vạch gióng thoáng đãng, tinh tế)
  const { niceMax, ticks } = useMemo(() => {
    if (chartData.length === 0) {
      return {
        niceMax: 0,
        ticks: [0],
      };
    }

    const rawMax = Math.max(...chartData.map((d) => d.amount), 0);

    if (rawMax === 0) {
      return {
        niceMax: 1000000,
        ticks: [0, 500000, 1000000],
      };
    }

    const targetStep = rawMax / 2;
    const power = Math.pow(10, Math.floor(Math.log10(targetStep)));
    const fraction = targetStep / power;

    let niceFraction = 1;
    if (fraction <= 1) niceFraction = 1;
    else if (fraction <= 2) niceFraction = 2;
    else if (fraction <= 2.5) niceFraction = 2.5;
    else if (fraction <= 5) niceFraction = 5;
    else niceFraction = 10;

    const step = niceFraction * power;
    const computedNiceMax = step * 2;
    const tickList = [0, step, step * 2];

    return {
      niceMax: computedNiceMax,
      ticks: tickList,
    };
  }, [chartData]);

  // Tọa độ SVG chuẩn tỉ lệ, không bị phóng đại quá to
  const svgWidth = 900;
  const svgHeight = 150;
  const paddingLeft = 52;
  const paddingRight = 16;
  const paddingTop = 14;
  const paddingBottom = 22;

  const chartAreaWidth = svgWidth - paddingLeft - paddingRight;
  const chartAreaHeight = svgHeight - paddingTop - paddingBottom;
  const baselineY = svgHeight - paddingBottom;

  const getX = (index) => {
    if (chartData.length <= 1) return paddingLeft + chartAreaWidth / 2;
    return paddingLeft + (index * chartAreaWidth) / (chartData.length - 1);
  };

  const getY = (val) => {
    if (niceMax <= 0) return baselineY;
    const clamped = Math.max(0, Math.min(val, niceMax));
    return baselineY - (clamped / niceMax) * chartAreaHeight;
  };

  const points = useMemo(() => {
    return chartData.map((pt, i) => ({
      x: getX(i),
      y: getY(pt.amount),
      data: pt,
    }));
  }, [chartData, niceMax]);

  // Spline Path (Cubic Bezier curve)
  const splinePath = useMemo(() => {
    if (points.length === 0) return "";
    if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;

    let path = `M ${points[0].x.toFixed(1)} ${points[0].y.toFixed(1)}`;
    for (let i = 0; i < points.length - 1; i++) {
      const p0 = points[i === 0 ? 0 : i - 1];
      const p1 = points[i];
      const p2 = points[i + 1];
      const p3 = points[i + 2] || p2;

      const tension = 0.2;
      const cp1x = p1.x + (p2.x - p0.x) * tension;
      const cp1y = p1.y + (p2.y - p0.y) * tension;
      const cp2x = p2.x - (p3.x - p1.x) * tension;
      const cp2y = p2.y - (p3.y - p1.y) * tension;

      path += ` C ${cp1x.toFixed(1)} ${cp1y.toFixed(1)}, ${cp2x.toFixed(
        1
      )} ${cp2y.toFixed(1)}, ${p2.x.toFixed(1)} ${p2.y.toFixed(1)}`;
    }
    return path;
  }, [points]);

  const areaPath = useMemo(() => {
    if (points.length === 0) return "";
    return `${splinePath} L ${points[points.length - 1].x.toFixed(
      1
    )} ${baselineY} L ${points[0].x.toFixed(1)} ${baselineY} Z`;
  }, [splinePath, points, baselineY]);

  const activePoint = hoveredIndex !== null ? points[hoveredIndex] : null;

  // Tooltip
  const tooltipWidth = 140;
  const tooltipHeight = 36;
  let tooltipX = 0;
  let tooltipY = 0;

  if (activePoint) {
    tooltipX = activePoint.x - tooltipWidth / 2;
    if (tooltipX < paddingLeft) tooltipX = paddingLeft;
    if (tooltipX + tooltipWidth > svgWidth - paddingRight) {
      tooltipX = svgWidth - paddingRight - tooltipWidth;
    }
    tooltipY = Math.max(4, activePoint.y - tooltipHeight - 6);
  }

  return (
    <div
      className="!font-inter !bg-white !rounded-2xl !p-3.5 sm:!p-4 !shadow-xs !border !border-slate-200/90 !space-y-3"
      style={fontStyle}
    >
      {/* Header Biểu Đồ gọn gàng */}
      <div className="!flex !flex-col sm:!flex-row !items-start sm:!items-center !justify-between !gap-2" style={fontStyle}>
        <div className="!flex !items-center !gap-2.5" style={fontStyle}>
          <div className="!w-8 !h-8 !rounded-lg !bg-blue-50 !border !border-blue-200 !text-blue-700 !flex !items-center !justify-center !shrink-0">
            <svg className="!w-4 !h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
          </div>
          <div>
            <h3 className="!text-sm sm:!text-base !font-extrabold !text-slate-800 !leading-tight" style={fontStyle}>
              Biểu Đồ Tiền Tồn Kho Theo Kỳ
            </h3>
            <p className="!text-sm !font-medium !text-slate-500 !mt-0.5" style={fontStyle}>
              Từ ngày: <strong className="!text-slate-700">{detailData?.from_date || "-"}</strong> &bull; Đến ngày: <strong className="!text-slate-700">{detailData?.to_date || "-"}</strong>
            </p>
          </div>
        </div>
      </div>

      {/* SVG Chart thanh thoát, không chiếm nhiều diện tích */}
      <div className="!relative !w-full !overflow-x-auto !select-none" style={fontStyle}>
        {chartData.length === 0 && !isLoading ? (
          <div className="!py-10 !text-center !text-slate-400 !text-sm" style={fontStyle}>
            Không có dữ liệu tồn kho.
          </div>
        ) : (
          <svg
            viewBox={`0 0 ${svgWidth} ${svgHeight}`}
            className="!w-full !h-auto !min-w-[480px]"
            style={fontStyle}
          >
            <defs>
              <linearGradient id="invValueGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#0284c7" stopOpacity="0.20" />
                <stop offset="85%" stopColor="#0284c7" stopOpacity="0.03" />
                <stop offset="100%" stopColor="#0284c7" stopOpacity="0.0" />
              </linearGradient>
            </defs>

            {/* Trục Y: Grid lines & Labels */}
            {ticks.map((tickVal) => {
              const y = getY(tickVal);
              return (
                <g key={tickVal}>
                  <line
                    x1={paddingLeft}
                    y1={y}
                    x2={svgWidth - paddingRight}
                    y2={y}
                    stroke="#f1f5f9"
                    strokeWidth="1"
                    strokeDasharray={tickVal === 0 ? "none" : "3 3"}
                  />
                  <text
                    x={paddingLeft - 6}
                    y={y + 3}
                    fill="#94a3b8"
                    fontSize="9.5"
                    fontWeight="500"
                    textAnchor="end"
                    fontFamily="Inter, sans-serif"
                    style={fontStyle}
                  >
                    {formatShortMoney(tickVal)}
                  </text>
                </g>
              );
            })}

            {/* Gradient Area Fill */}
            <path d={areaPath} fill="url(#invValueGradient)" />

            {/* Line Spline Stroke */}
            <path
              d={splinePath}
              fill="none"
              stroke="#0284c7"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />

            {/* Trục X: Labels */}
            {chartData.map((pt, i) => {
              const isSelected = hoveredIndex === i;
              return (
                <g key={i}>
                  <text
                    x={getX(i)}
                    y={svgHeight - 6}
                    fill={isSelected ? "#0284c7" : "#64748b"}
                    fontSize="10"
                    fontWeight={isSelected ? "800" : "500"}
                    textAnchor="middle"
                    fontFamily="Inter, sans-serif"
                    style={fontStyle}
                  >
                    {pt.label}
                  </text>
                </g>
              );
            })}

            {/* Hover Points */}
            {points.map((pt, i) => {
              const isHovered = hoveredIndex === i;
              return (
                <g
                  key={i}
                  className="cursor-pointer"
                  onMouseEnter={() => setHoveredIndex(i)}
                  onMouseLeave={() => setHoveredIndex(null)}
                >
                  <circle cx={pt.x} cy={pt.y} r="15" fill="transparent" />
                  {isHovered && (
                    <circle
                      cx={pt.x}
                      cy={pt.y}
                      r="6.5"
                      fill="#0284c7"
                      opacity="0.22"
                    />
                  )}
                  <circle
                    cx={pt.x}
                    cy={pt.y}
                    r={isHovered ? "4" : "2.8"}
                    fill="#ffffff"
                    stroke="#0284c7"
                    strokeWidth="1.8"
                  />
                </g>
              );
            })}

            {/* Tooltip */}
            {activePoint && (
              <g className="pointer-events-none">
                <line
                  x1={activePoint.x}
                  y1={paddingTop}
                  x2={activePoint.x}
                  y2={baselineY}
                  stroke="#0284c7"
                  strokeWidth="1"
                  strokeDasharray="3 3"
                  opacity="0.5"
                />
                <rect
                  x={tooltipX}
                  y={tooltipY}
                  width={tooltipWidth}
                  height={tooltipHeight}
                  rx="5"
                  fill="#1e293b"
                  opacity="0.94"
                />
                <text
                  x={tooltipX + tooltipWidth / 2}
                  y={tooltipY + 13}
                  fill="#7dd3fc"
                  fontSize="9.5"
                  fontWeight="500"
                  textAnchor="middle"
                  fontFamily="Inter, sans-serif"
                  style={fontStyle}
                >
                  Kỳ: {activePoint.data.period} ({activePoint.data.value_date})
                </text>
                <text
                  x={tooltipX + tooltipWidth / 2}
                  y={tooltipY + 27}
                  fill="#ffffff"
                  fontSize="10.5"
                  fontWeight="700"
                  textAnchor="middle"
                  fontFamily="Inter, sans-serif"
                  style={fontStyle}
                >
                  {formatCurrency(activePoint.data.amount)}
                </text>
              </g>
            )}
          </svg>
        )}
      </div>
    </div>
  );
}
